import { type ReactNode, useState } from "react";

import { Icon } from "@core/components/Icon";
import { IconButton } from "@core/components/IconButton";
import type { LinkAnchorProps, LinkButtonProps } from "@core/components/Link";
import { Link } from "@core/components/Link";
import classnames from "classnames";

import type { DirectionalIconComponent } from "@jti/brand-icons";
import { UiCheckCircleIcon } from "@jti/brand-icons/ui-check-circle";
import { UiCrossIcon } from "@jti/brand-icons/ui-cross";
import { UiCrossCircleIcon } from "@jti/brand-icons/ui-cross-circle";
import { UiInfoCircleBoldIcon } from "@jti/brand-icons/ui-info-circle-bold";
import { UiWarningIcon } from "@jti/brand-icons/ui-warning";

import type { SafeHtmlAttributes } from "../../types/SafeHtmlAttributes";

import styles from "./AlertBanner.module.scss";

export const ALERT_BANNER_TONES = ["info", "success", "warning", "error"] as const;

export type AlertBannerTone = (typeof ALERT_BANNER_TONES)[number];

type OmittedLinkProps = "children" | "size" | "label";
export type AlertBannerAction = (Omit<LinkAnchorProps, OmittedLinkProps> | Omit<LinkButtonProps, OmittedLinkProps>) & {
  label: string;
};

type AlertBannerBaseProps = Omit<SafeHtmlAttributes<HTMLDivElement>, "title"> & {
  tone?: AlertBannerTone;
  title?: string;
  /**
   * Added just to not break legacy usages of `AlertBanner` in `packages/ui`.
   * This prop is not type-safe and should be avoided.
   * Use only as a last resort — prefer `title` with a string label.
   *
   * @deprecated Pass a string via `title` instead
   */
  richTitle?: ReactNode;
  description?: string;
  /**
   * Added just to not break legacy usages of `AlertBanner` in `packages/ui`.
   * This prop is not type-safe and should be avoided.
   * Use only as a last resort — prefer `description` with a string label.
   *
   * @deprecated Pass a string via `description` instead
   */
  richDescription?: ReactNode;
  actions?: [AlertBannerAction] | [AlertBannerAction, AlertBannerAction];
};

type DismissibleAlertBannerProps = AlertBannerBaseProps & {
  isDismissible: true;
  onDismiss?: () => void;
  dismissLabel: string;
};

type NonDismissibleAlertBannerProps = AlertBannerBaseProps & {
  isDismissible?: false;
  onDismiss?: never;
  dismissLabel?: never;
};

export type AlertBannerProps = DismissibleAlertBannerProps | NonDismissibleAlertBannerProps;

const toneIconMap: Record<AlertBannerTone, DirectionalIconComponent> = {
  info: UiInfoCircleBoldIcon,
  success: UiCheckCircleIcon,
  warning: UiWarningIcon,
  error: UiCrossCircleIcon,
};

export const AlertBanner = (props: AlertBannerProps) => {
  const {
    tone = "info",
    title,
    richTitle,
    description,
    richDescription,
    actions,
    isDismissible,
    onDismiss,
    dismissLabel,
    role = "status",
    ...rest
  } = props as DismissibleAlertBannerProps;
  const ToneIcon = toneIconMap[tone];
  const titleContent = title ?? richTitle;
  const descriptionContent = description ?? richDescription;
  const [isVisible, setIsVisible] = useState(true);

  const handleDismiss = () => {
    setIsVisible(false);
    onDismiss?.();
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div {...rest} className={classnames(styles.alertBanner, styles[tone])} role={role}>
      <div className={styles.iconWrapper}>
        <Icon icon={ToneIcon} size="md" />
      </div>

      <div className={styles.contentWrapper}>
        <div className={styles.textWrapper}>
          {titleContent && <p className={styles.title}>{titleContent}</p>}
          {descriptionContent && <p className={styles.description}>{descriptionContent}</p>}
        </div>
        {actions && (
          <div className={styles.actions}>
            {actions.map(({ label, ...action }, index) => (
              <Link key={index} {...action} size="md">
                {label}
              </Link>
            ))}
          </div>
        )}
      </div>

      {isDismissible && (
        <div className={styles.closeWrapper}>
          <IconButton icon={UiCrossIcon} compact aria-label={dismissLabel} onClick={handleDismiss} />
        </div>
      )}
    </div>
  );
};
